import jsPDF from "jspdf";
import { getSummaryBox } from "./getSummaryBox";
import estressRecomendations from "../data/estressRecomendations";

const COLORS = {
  white: "#FFFFFF",
  delilife: "#00be9c",
  dark: "#042330",
  bg: "#f1f6f4",
  border: "#d7dde2",
  muted: "#76797c"
};

const BURNOUT_GENERAL_RECS = [
  "Pausas Laborales: tómate 5 min de relajación aproximadamente cada hora.",
  "Organiza tu agenda y tus pendientes: prioriza tareas y evita sobrecarga.",
  "Habilidades blandas: practica comunicación asertiva y pide ayuda cuando sea necesario.",
  "Estilo de vida saludable: regula sueño, alimentación y ejercicio para mitigar estrés.",
  "Busca ayuda profesional si los síntomas persisten más de dos meses."
];

const getBurnoutSummary = (stressResults = {}) => [
  {
    title: "Nivel de estrés físico",
    textRisk:
      {
        0: "Sin Estrés",
        1: "Estrés Leve",
        2: "Estrés Medio",
        3: "Estrés Alto",
        4: "Estrés Grave"
      }[stressResults.physicalStress] || "Sin dato",
    boxColor:
      {
        0: "#00BE9C",
        1: "#FFC107",
        2: "#FFC107",
        3: "#FF5252",
        4: "#FF5252"
      }[stressResults.physicalStress] || "#AAAAAA"
  },
  {
    title: "Indicador de Burnout",
    textRisk: stressResults.hasBurnOut ? "Positivo" : "Negativo",
    boxColor: stressResults.hasBurnOut ? "#FF5252" : "#00BE9C"
  },
  {
    title: "Agotamiento emocional",
    textRisk:
      {
        0: "Agotamiento Bajo",
        1: "Agotamiento Medio",
        2: "Agotamiento Alto"
      }[stressResults.exhaustionLevel] || "Sin dato",
    boxColor:
      {
        0: "#00BE9C",
        1: "#FFC107",
        2: "#FF5252"
      }[stressResults.exhaustionLevel] || "#AAAAAA"
  },
  {
    title: "Desconexión laboral",
    textRisk:
      {
        0: "Baja",
        1: "Media",
        2: "Alta"
      }[stressResults.cynicismLevel] || "Sin dato",
    boxColor:
      {
        0: "#00BE9C",
        1: "#FFC107",
        2: "#FF5252"
      }[stressResults.cynicismLevel] || "#AAAAAA"
  },
  {
    title: "Eficacia profesional",
    textRisk:
      {
        0: "Eficacia Alto",
        1: "Eficacia Medio",
        2: "Eficacia Bajo"
      }[stressResults.efficacyLevel] || "Sin dato",
    boxColor:
      {
        0: "#00BE9C",
        1: "#FFC107",
        2: "#FF5252"
      }[stressResults.efficacyLevel] || "#AAAAAA"
  }
];

function drawTopHeader(doc, title) {
  const w = doc.internal.pageSize.getWidth();
  doc.setFillColor(COLORS.dark);
  doc.rect(0, 0, w, 22, "F");
  doc.setTextColor(COLORS.white);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("DeliLifeWellness", w / 2, 13, { align: "center" });

  doc.setTextColor(COLORS.delilife);
  doc.setFontSize(15);
  doc.text(title, w / 2, 34, { align: "center" });
}

function drawSummaryGrid(doc, items, startY, cols = 2) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 16;
  const cellW = (pageWidth - margin * 2) / cols;
  const lineHeight = doc.getLineHeightFactor();
  let y = startY;

  items.forEach((item, idx) => {
    const col = idx % cols;
    if (col === 0 && idx > 0) {
      y += 28;
    }

    const x = margin + col * cellW;

    doc.setTextColor(COLORS.dark);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(item.title, x + cellW / 2, y, { align: "center" });

    const boxY = y + 3;
    const boxX = x + 5;
    const boxW = cellW - 10;
    const boxH = 12;

    doc.setFillColor(item.boxColor || "#AAAAAA");
    doc.setDrawColor(item.boxColor || "#AAAAAA");
    doc.roundedRect(boxX, boxY, boxW, boxH, 2, 2, "FD");

    doc.setTextColor(COLORS.dark);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    const textY = boxY + boxH / 2 + (10 * lineHeight) / 4;
    doc.text(item.textRisk || "Sin dato", boxX + boxW / 2, textY, { align: "center" });
  });

  return y + 20;
}

function drawRecommendationBlocks(doc, title, sections, startY = 48) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 16;
  const maxW = pageWidth - margin * 2;
  let y = startY;

  doc.setFillColor(COLORS.dark);
  doc.rect(0, 0, pageWidth, 20, "F");
  doc.setTextColor(COLORS.white);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text(title, margin, 13);

  sections.forEach((section) => {
    if (y > pageHeight - 30) {
      doc.addPage();
      y = 24;
    }

    doc.setFillColor(COLORS.dark);
    doc.rect(margin, y, maxW, 8, "F");
    doc.setTextColor(COLORS.white);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(section.title, margin + 2, y + 5.5);
    y += 12;

    doc.setTextColor(COLORS.dark);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);

    const lines = doc.splitTextToSize(section.text || "Sin recomendación.", maxW);
    lines.forEach((line) => {
      if (y > pageHeight - 16) {
        doc.addPage();
        y = 20;
      }
      doc.text(line, margin, y);
      y += 5;
    });

    y += 4;
  });
}

export function createHbPdfBlobUrl({ hraResults = {}, stressResults = {}, hraRecommendations = {} }) {
  const doc = new jsPDF();

  drawTopHeader(doc, "Resultados HRA & Burnout");

  const hraSummary = [
    getSummaryBox("overall", hraResults.overallEvaluation),
    getSummaryBox("nutrition", hraResults.nutritionalState),
    getSummaryBox("work", hraResults.workHappiness),
    getSummaryBox("metabolic", hraResults.metabolicRisk),
    getSummaryBox("activity", hraResults.physicalActivityEvaluation),
    getSummaryBox("lifestyle", hraResults.lifeStyle)
  ];

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(COLORS.dark);
  doc.text("Sección HRA", 16, 46);
  drawSummaryGrid(doc, hraSummary, 54, 2);

  doc.addPage();
  drawRecommendationBlocks(
    doc,
    "Recomendaciones HRA",
    [
      { title: "Evaluación General", text: hraRecommendations.overallEvaluation },
      { title: "Índice de Masa Corporal", text: hraRecommendations.nutritionalState },
      { title: "Felicidad en el trabajo", text: hraRecommendations.workHappiness },
      { title: "Riesgo Metabólico", text: hraRecommendations.metabolicRisk },
      { title: "Nivel de sedentarismo", text: hraRecommendations.physicalActivityEvaluation },
      { title: "Estilo de vida", text: hraRecommendations.lifeStyle }
    ],
    30
  );

  doc.addPage();
  drawTopHeader(doc, "Resultados Burnout");
  const burnoutSummary = getBurnoutSummary(stressResults);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(COLORS.dark);
  doc.text("Sección Estrés y Burnout", 16, 46);
  const stressSummaryEndY = drawSummaryGrid(doc, burnoutSummary, 54, 2);

  const stressRec = estressRecomendations[stressResults.physicalStress];

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(COLORS.dark);
  doc.text("Recomendación principal por nivel de estrés físico", 16, stressSummaryEndY + 8);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(COLORS.muted);
  const recLines = doc.splitTextToSize(stressRec || "Sin recomendación.", 178);
  let recY = stressSummaryEndY + 14;
  recLines.forEach((line) => {
    if (recY > 278) {
      doc.addPage();
      recY = 20;
    }
    doc.text(line, 16, recY);
    recY += 5;
  });

  doc.addPage();
  drawRecommendationBlocks(
    doc,
    "Recomendaciones Generales de Burnout",
    BURNOUT_GENERAL_RECS.map((text, idx) => ({ title: `Recomendación ${idx + 1}`, text })),
    30
  );

  return doc.output("bloburl");
}
